import { IntersectionFade } from "../components/IntersectionFade";

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const PROBLEMS = [
  {
    title: "Paywalled on every platform",
    detail: "Every device — Android, Windows, Pi — costs extra. Budgets blow up before a single screen plays.",
  },
  {
    title: "Poll-based sync lags 30–60 seconds",
    detail: "Content updates sit in a queue. Urgent campaigns don't land in time.",
  },
  {
    title: "Offline means 'broken'",
    detail: "Network hiccup → black screen. Reconnect → cache storm. Players weren't built for edge reliability.",
  },
  {
    title: "Proof-of-play costs extra",
    detail: "Analytics that prove ROI sit behind paid tiers — the very thing that justifies the budget.",
  },
] as const;

const SOLUTIONS = [
  {
    title: "One free engine for every platform",
    detail: "Web, Electron, Tauri, Android TV, Raspberry Pi — all running the same open TypeScript core.",
  },
  {
    title: "WebSocket push — instant updates",
    detail: "KSP pushes content changes the moment you publish. Sub-second propagation via Cloudflare Durable Objects.",
  },
  {
    title: "Content-addressed offline cache",
    detail: "Every asset is sha256-verified and atomically swapped in. Network loss means nothing — players keep going.",
  },
  {
    title: "Proof-of-play, always free",
    detail: "Built-in telemetry, batched and de-duped server-side. Every play is logged, no tier upgrade needed.",
  },
] as const;

export function ProblemSolution() {
  return (
    <section className="section" id="problem-solution" aria-labelledby="problem-solution-heading">
      <div className="container">
        <IntersectionFade>
          <div className="section-header">
            <span className="eyebrow">Why Kynetra</span>
            <h2 className="h2" id="problem-solution-heading">
              Legacy signage is{" "}
              <span className="grad-text">holding you back</span>
            </h2>
            <p className="lead">
              The open-source field breaks down on at least one axis — cost, latency, reliability, or analytics.
              Kynetra Signage targets all of them at once.
            </p>
          </div>
        </IntersectionFade>

        <div className="problem-grid">
          {/* Problems */}
          <div>
            <IntersectionFade delay={0} direction="left">
              <p className="eyebrow mb-16" style={{ color: "#e05252" }}>The status quo</p>
            </IntersectionFade>
            <ul className="problem-list" aria-label="Legacy signage problems">
              {PROBLEMS.map(({ title, detail }, i) => (
                <IntersectionFade key={title} delay={i * 60} direction="left">
                  <li className="problem-item">
                    <span className="problem-item-icon bad" aria-hidden="true">
                      <XIcon />
                    </span>
                    <div className="problem-item-text">
                      <strong>{title}</strong>
                      <span>{detail}</span>
                    </div>
                  </li>
                </IntersectionFade>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <IntersectionFade delay={0} direction="right">
              <p className="eyebrow mb-16" style={{ color: "var(--success)" }}>Kynetra fixes it</p>
            </IntersectionFade>
            <ul className="problem-list" aria-label="Kynetra Signage solutions">
              {SOLUTIONS.map(({ title, detail }, i) => (
                <IntersectionFade key={title} delay={i * 60} direction="right">
                  <li className="problem-item">
                    <span className="problem-item-icon good" aria-hidden="true">
                      <CheckIcon />
                    </span>
                    <div className="problem-item-text">
                      <strong>{title}</strong>
                      <span>{detail}</span>
                    </div>
                  </li>
                </IntersectionFade>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
